import { Controller } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import {
  ButtonSecondary,
  ButtonSize,
  formatBalance,
  IconExpand,
  IconLoader,
  Select,
  SelectSize,
} from '@ui/src';
import StepPartBlock from '../../../common/step-part-block';
import { NewApplicationStatus } from '../../../../../hoc/new-application/types';
import SourceOfIncomeItem from './source-of-income-item';
import InfoRow from '../../../common/info-row';
import useSourceOfIncome from '../../hooks/use-source-of-income';
import { SourceOfIncomeTypes } from '../types';

import cn from '../../styles.module.css';
import commonStyles from '../../../common/styles.module.css';

const SourceOfIncome = ({
  sourceOfIncome,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  status,
  isEditable,
  questionnarieSetValue,
  questionnarieFields,
  baseCurrency,
  questionnarieIsLoading,
}: SourceOfIncomeTypes) => {
  const { sourceOfIncomeFiltered, totalValue } = useSourceOfIncome({
    sourceOfIncome,
    questionnarieFields,
  });

  return (
    <StepPartBlock title="Source of income" zIndex={25}>
      {questionnarieIsLoading && (
        <div className={commonStyles.dictionaryLoader}>
          <IconLoader />
        </div>
      )}
      {!questionnarieIsLoading && (
        <>
          <InfoRow
            title="Require"
            id="requiresSourcesOfIncomes"
            style={{
              position: 'relative',
              zIndex: 17,
            }}
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="requiresSourcesOfIncomes"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  name={name}
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['requiresSourcesOfIncomes']);

                    if (e.target.value === 'no') {
                      questionnarieClearErrors([
                        'sourcesOfIncomes',
                        'sourcesOfIncomesBorrowedOther',
                        'sourcesOfIncomesOther',
                      ]);

                      questionnarieSetValue('sourcesOfIncomes', []);
                      questionnarieSetValue(
                        'sourcesOfIncomesBorrowedOther',
                        '',
                      );
                      questionnarieSetValue('sourcesOfIncomesOther', '');
                    }
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.requiresSourcesOfIncomes?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>

          {questionnarieFields?.requiresSourcesOfIncomes === 'yes' && (
            <>
              <div className={cn.sourceOfIncomeTitle}>
                Mandatory for high-risk cases only
              </div>

              <div className={cn.sourcesOfIncomeList}>
                {(questionnarieFields?.sourcesOfIncomes?.length as number) >
                  0 &&
                  questionnarieFields?.sourcesOfIncomes?.map((item) => (
                    <SourceOfIncomeItem
                      key={item?.id || item.sourceId}
                      sourceOfIncomes={sourceOfIncome}
                      sourceOfIncomeFiltered={sourceOfIncomeFiltered}
                      sourceOfIncome={{
                        id: item?.id || item.sourceId,
                        ...item,
                      }}
                      sourceOfIncomeValues={
                        questionnarieFields?.sourcesOfIncomes as {
                          id?: string;
                          value: string;
                          sourceId: string;
                        }[]
                      }
                      questionnarieSetValue={questionnarieSetValue}
                      baseCurrency={baseCurrency}
                      questionnarieControl={questionnarieControl}
                      questionnarieErrors={questionnarieErrors}
                      questionnarieClearErrors={questionnarieClearErrors}
                      isEditable={isEditable}
                      status={status}
                    />
                  ))}
              </div>
              {(questionnarieFields?.sourcesOfIncomes?.filter(
                (item) => item.sourceId,
              )?.length as number) > 0 && (
                <div className={cn.sourceOfIncomeTotal}>
                  {`Total Annual Income: ${formatBalance(totalValue, baseCurrency)} ${baseCurrency?.name}`}
                </div>
              )}
              {sourceOfIncomeFiltered &&
                sourceOfIncomeFiltered?.length > 0 &&
                status !== NewApplicationStatus.ACCEPTED &&
                status !== NewApplicationStatus.DECLINED && (
                  <div className={cn.addSourceButtonWrapper}>
                    <ButtonSecondary
                      text="Add Source"
                      icon={<IconExpand width={16} height={16} />}
                      size={ButtonSize.SMALL}
                      className={`button-white ${cn.addSourceButton}`}
                      onClick={() => {
                        const nextSourceOfIncome = (
                          [] as {
                            sourceId: string;
                            value: string | number;
                            id?: string;
                          }[]
                        ).concat(
                          questionnarieFields?.sourcesOfIncomes as {
                            id?: string;
                            sourceId: string;
                            value: string | number;
                          }[],
                        );

                        nextSourceOfIncome.push({
                          id: `source-of-income-${uuid()}`,
                          sourceId: '',
                          value: '',
                        });

                        questionnarieSetValue(
                          'sourcesOfIncomes',
                          nextSourceOfIncome,
                        );
                      }}
                    />
                  </div>
                )}
            </>
          )}
          {questionnarieErrors?.sourcesOfIncomes?.message && (
            <div className={cn.stepOneQuestionnaireError}>
              {questionnarieErrors?.sourcesOfIncomes?.message}
            </div>
          )}
          <Controller
            control={questionnarieControl}
            name="sourcesOfIncomes"
            defaultValue={[]}
            render={({ field: { name } }) => (
              <input type="hidden" name={name} />
            )}
          />
        </>
      )}
    </StepPartBlock>
  );
};

export default SourceOfIncome;
